'use client'

import { Grid, GridType } from '@components/grid'
import { CommonFilterPanel, FilterPanelAnimation } from '@components/grid/filter-panel'
import { Button } from '@components/button'
import { SoloViewerTokenFragment, GetProjectFiltersQuery } from '@/gql/generated/graphql'
import { ShowcaseEditGridItem } from './showcase-edit-grid-item'
import { BodyText } from 'design-configuration/components'
import { TextButton } from '@components/text-button'
import { useShowcaseEditorFilters } from './use-showcase-editor-filters'
import { useMemo } from 'react'
import { Omnisearch } from '@components/omnisearch'

type ShowcaseEditorViewProps = {
  userTokens: SoloViewerTokenFragment[]
  selectedTokenIds: string[]
  isSaving: boolean
  userTokensFetching: boolean
  visibleTokenCount: number
  projectFiltersData?: GetProjectFiltersQuery
  onToggleToken: (tokenId: string) => void
  onDeselectAll: () => void
  onSave: () => void
  onShowMore: () => void
}

export const ShowcaseEditorView = ({
  userTokens,
  selectedTokenIds,
  isSaving,
  userTokensFetching,
  visibleTokenCount,
  projectFiltersData,
  onToggleToken,
  onDeselectAll,
  onSave,
  onShowMore
}: ShowcaseEditorViewProps): React.ReactElement => {
  // Initialize filtering
  const {
    sortOrder,
    sortOptions,
    setSortOrder,
    tagsFilter,
    categoryFilter,
    clearFilters,
    numberOfActiveFilters,
    searchValue,
    setSearchValue,
    searchTerms,
    handleSaveSearchTerm,
    handleRemoveSearchTerm
  } = useShowcaseEditorFilters({
    projectFiltersData
  })

  // Filter user tokens based on current filter values
  const filteredUserTokens = useMemo(() => {
    let filtered = userTokens

    // Apply filters if any are active
    if (tagsFilter.selectedOptions.length > 0 || categoryFilter.selectedOptions.length > 0 || searchTerms.length > 0) {
      filtered = userTokens.filter((token) => {
        if (!token.project) return false

        let passesFilter = true

        // Apply search filters - similar to user token grid logic
        if (searchTerms.length > 0) {
          const validSearchTerms = searchTerms.filter((term) => term.trim())

          if (validSearchTerms.length > 0) {
            const passesSearch = validSearchTerms.some((searchTerm) => {
              const trimmedSearch = searchTerm.trim().toLowerCase()

              // Check if search term is a number (for token search)
              const isNumericSearch = /^\d+$/.test(trimmedSearch)

              if (isNumericSearch) {
                // Search by token number (invocation)
                return token.invocation?.toString() === trimmedSearch
              } else {
                // Search by project name or artist name
                const projectName = token.project?.name?.toLowerCase() || ''
                const artistName = token.project?.artist_name?.toLowerCase() || ''

                return projectName.includes(trimmedSearch) || artistName.includes(trimmedSearch)
              }
            })

            if (!passesSearch) passesFilter = false
          }
        }

        // Apply tags filter - check if project has any of the selected tags
        if (tagsFilter.selectedOptions.length > 0) {
          const projectTags = (token.project as any)?.tags?.map((t: any) => t.tag_name) ?? []
          const hasSelectedTag = tagsFilter.selectedOptions.some((selectedTag) => projectTags.includes(selectedTag))
          if (!hasSelectedTag) passesFilter = false
        }

        // Apply category filter - check if project vertical matches any selected categories
        if (categoryFilter.selectedOptions.length > 0) {
          // Get the project's vertical using the same logic as the main token grid
          let projectVertical = token.project.vertical_name

          // Handle special cases like engine projects
          if (projectVertical === 'flex' || projectVertical === 'fullyonchain') {
            projectVertical = 'engine'
          } else if (token.project.vertical?.category_name === 'collaborations') {
            projectVertical = 'collaborations'
          }

          if (!projectVertical || !categoryFilter.selectedOptions.includes(projectVertical)) {
            passesFilter = false
          }
        }

        return passesFilter
      })
    }

    return filtered
  }, [userTokens, tagsFilter.selectedOptions, categoryFilter.selectedOptions, searchTerms])

  // Apply sorting to filtered tokens
  const sortedFilteredTokens = useMemo(() => {
    const sorted = [...filteredUserTokens]

    sorted.sort((a, b) => {
      switch (sortOrder) {
        case 'Recently collected': {
          // Sort by last_transferred_at descending (most recent first)
          const aTransferred = new Date(a.last_transferred_at || 0).getTime()
          const bTransferred = new Date(b.last_transferred_at || 0).getTime()
          return bTransferred - aTransferred
        }

        case 'First collected': {
          // Sort by last_transferred_at ascending (oldest first)
          const aTransferred = new Date(a.last_transferred_at || 0).getTime()
          const bTransferred = new Date(b.last_transferred_at || 0).getTime()
          return aTransferred - bTransferred
        }

        case 'Oldest collection': {
          // Sort by minted_at ascending (oldest minted first)
          const aMinted = new Date(a.minted_at || 0).getTime()
          const bMinted = new Date(b.minted_at || 0).getTime()
          return aMinted - bMinted
        }

        case 'Newest collection':
        default: {
          // Sort by minted_at descending (newest minted first)
          const aMinted = new Date(a.minted_at || 0).getTime()
          const bMinted = new Date(b.minted_at || 0).getTime()
          return bMinted - aMinted
        }
      }
    })

    return sorted
  }, [filteredUserTokens, sortOrder])

  // Get only the visible tokens based on the current pagination
  const visibleTokens = sortedFilteredTokens.slice(0, visibleTokenCount)
  const canShowMore = visibleTokenCount < sortedFilteredTokens.length

  return (
    <div id="user-showcase" className="w-full max-w-full overflow-hidden">
      <div className="[&>*]:min-w-full [&>*]:max-w-full">
        <div className="p-sm flex items-center justify-between">
          <div>
            <BodyText>Artworks will display in selection order ({selectedTokenIds.length} selected)</BodyText>
          </div>
          <div className="gap-x-sm flex">
            <TextButton
              onClick={onDeselectAll}
              disabled={selectedTokenIds.length === 0 || isSaving}
              className="text-on-surface-variant"
            >
              Deselect all
            </TextButton>
            <Button colorTheme="outline" size="small" onClick={onSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
        <Grid
          name={'user-tokens-edit'}
          gridType={GridType.Tokens}
          sortOrder={sortOrder}
          sortOptions={sortOptions}
          onSortOrderChange={setSortOrder}
          // use "Show more" instead of pagination on editor view
          onPageNumberChange={() => undefined}
          pageNumber={1}
          totalPages={1}
          totalItems={visibleTokens.length}
          numberOfActiveFilters={numberOfActiveFilters}
          onClearFilters={clearFilters}
          gridItems={visibleTokens}
          gridItemComponent={(item: SoloViewerTokenFragment) => (
            <ShowcaseEditGridItem
              token={item}
              isSelected={selectedTokenIds.includes(item.id)}
              selectedIndex={selectedTokenIds.indexOf(item.id)}
              onToggle={onToggleToken}
            />
          )}
          filterLayout={<CommonFilterPanel filters={[categoryFilter, tagsFilter]} />}
          filterAnimation={FilterPanelAnimation.FadeIn}
          fetching={userTokensFetching}
          gridClassNameOverride="gap-md relative grid h-fit grid-flow-row auto-rows-min grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          className="px-sm pb-sm"
          searchControls={
            <Omnisearch
              value={searchValue}
              onChange={setSearchValue}
              savedValues={searchTerms}
              onSave={handleSaveSearchTerm}
              onRemove={handleRemoveSearchTerm}
              placeholder="Search by title or artist"
            />
          }
        />
        {canShowMore && (
          <div className="mt-lg pb-2xl flex justify-center">
            <Button colorTheme="outline" size="small" onClick={onShowMore} disabled={isSaving}>
              Show more
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
